// File: src/components/auth/AuthCard/AuthCard.tsx
// Purpose: A **modern, fully responsive** authentication card for login and sign-up screens.

import React from "react";
import styled from "styled-components";
import { Typography } from "../../general";
import defaultLogoSrc from "../../../assets/logo.svg"; // ✅ USHI Logo
import { Flex } from "../../layouts";

interface AuthCardProps {
  title?: string; // ✅ Optional Title
  subtitle?: string; // ✅ Optional Subtitle
  children: React.ReactNode;
  background?: "gradient" | "image" | "none"; // ✅ Supports Gradient or Image Backgrounds
  backgroundImage?: string; // ✅ Custom Image Path (Only Applies If background="image")
  logo?: boolean | string; // ✅ Now supports true, false, or string

}

const AuthCardWrapper = styled(Flex).attrs({
  variant: "card",
  responsive: true,
  shouldAnimate: true,
})<AuthCardProps>`
  width: 100%;
  max-width: 800px;
  min-height: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.radii.lg};
  text-align: center;
  box-shadow: ${({ theme }) => theme.elevations.md};

  /* ✅ Dynamic Background Logic */
  background: ${({ background, backgroundImage , theme}) =>
    background === "gradient"
      ? `${theme.gradients.primary}` // ✅ USHI Themed Gradient
      : background === "image" && backgroundImage
      ? `url(${backgroundImage}) center/cover`
      : theme.colors.surface};

  background: ${({ background, backgroundImage , theme}) => theme.colors.surface};

  /* ✅ Explicit, Non-Overlapping Media Queries */
  @media (min-width: ${({ theme }) => theme.breakpoints.laptop}) and (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
     max-width: 720px;
    
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) and (max-width: ${({ theme }) => theme.breakpoints.laptop}) {
    max-width: 640px;
    padding: ${({ theme }) => theme.spacing.sm};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-width: 90%;
    flex-direction: column;
    padding: ${({ theme }) => theme.spacing.sm};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    max-width: 100%;
    flex-direction: column;
    padding: ${({ theme }) => theme.spacing.xs};
  }
`;

const LogoContainer = styled(Flex)`
  margin-bottom: ${({ theme }) => theme.spacing.md};

  img {
    width: 120px;
    height: auto;
    transition: transform 0.2s ease-in-out;
    
    &:hover {
      transform: scale(1.05);
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
      width: 120px;
    }
  }
`;

const ContentContainer = styled(Flex)`
  flex: 2 1 auto;
  padding: ${({ theme }) => theme.spacing.md};
  text-align: center;
  min-width: 80%;
  max-width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) and (max-width: ${({ theme }) => theme.breakpoints.laptop}) {
    max-width: 90%;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-width: 100%;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 100%;
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

export const AuthCard: React.FC<AuthCardProps> = ({
  title,
  subtitle,
  children,
  background = "none",
  backgroundImage,
  logo = true,
}) => {
  const logoToUse: string | undefined =
  typeof logo === "string"
    ? logo
    : logo === true
    ? defaultLogoSrc
    : undefined;
  return (
    <AuthCardWrapper background={background} backgroundImage={backgroundImage} >
      {logo && <LogoContainer align="center" justify="center">
        <img src={logoToUse} alt="Logo" />
        </LogoContainer>}

      <ContentContainer direction="column">
        {title && <Typography variant="heading1">{title}</Typography>}
        {subtitle && <Typography variant="bodyLarge" colorScheme="textSecondary">{subtitle}</Typography>}
        {children}
      </ContentContainer>
    </AuthCardWrapper>
  );
};